/**
 * useContainerWidth Hook for UI-Model-1
 *
 * Manages container width with:
 * - Debounced value updates (500ms)
 * - Lock/unlock toggle
 * - Width constraints (min 25%, max based on containers in row)
 * - Auto-distribution to other unlocked containers in same row
 */

import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { toast } from 'Components/DesignSystem/Toast/useToast';
import { useUIModelState } from '../contexts/UIModelStateContext';
import { useContainerOperations } from './useContainerOperations';
import { ContainerAPI } from '../types/state.types';
import { createUpdateContainerCommand, createBatchCommand } from '../types/command.types';
import { CONTAINER_CONSTRAINTS } from '../container-management/constants/constraints';
import { validateLockImpact } from '../container-management/utils/widthCalculations';

interface UseContainerWidthOptions {
  debounceDelay?: number;
  minWidth?: number;
}

interface UseContainerWidthReturn {
  /** Current local width value (for UI display) */
  localWidth: number;
  /** Whether width is locked */
  isLocked: boolean;
  /** Maximum width allowed based on containers in row */
  maxWidth: number;
  /** Minimum width (always 25%) */
  minWidth: number;
  /** Other containers in the same row */
  containersInRow: ContainerAPI[];
  /** Handle width change (debounced) */
  handleWidthChange: (value: number) => void;
  /** Toggle lock state */
  toggleLock: () => void;
  /** Reset all widths in the row to equal distribution */
  resetRowWidths: () => void;
  /** Manually set local width without triggering update */
  setLocalWidth: (value: number) => void;
}

/**
 * Parse width string to number (e.g., "50%" -> 50, "100px" -> 100)
 */
const parseWidth = (width: string | undefined): number => {
  if (!width) return 100;
  const match = width.match(/^(\d+(?:\.\d+)?)/);
  return match ? parseFloat(match[1]) : 100;
};

/**
 * Format width number to string with % suffix
 */
const formatWidth = (value: number): string => `${value}%`;

export const useContainerWidth = (
  containerId: string,
  options: UseContainerWidthOptions = {}
): UseContainerWidthReturn => {
  const { debounceDelay = 500, minWidth: minWidthOption = CONTAINER_CONSTRAINTS.MIN_WIDTH_PERCENT } = options;

  const { state, dispatch, getContainer, getContainersForCanvas } = useUIModelState();
  const { updateContainer } = useContainerOperations();

  // Get current container and its parent (if nested)
  const { element: container, path, parent: parentContainer } = getContainer(containerId);
  const canvasId = path?.canvasId;
  const isNestedContainer = parentContainer !== null;

  // Parse initial width from container configuration
  const initialWidth = parseWidth(container?.configuration?.width);
  const initialLocked = container?.configuration?.widthLocked ?? false;

  // Local state for immediate UI updates
  const [localWidth, setLocalWidth] = useState<number>(initialWidth);
  const [isLocked, setIsLocked] = useState<boolean>(initialLocked);

  // Debounce timer ref
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Sync local state when container changes externally (e.g., undo/redo)
  useEffect(() => {
    const newWidth = parseWidth(container?.configuration?.width);
    const newLocked = container?.configuration?.widthLocked ?? false;
    setLocalWidth(newWidth);
    setIsLocked(newLocked);
  }, [container?.configuration?.width, container?.configuration?.widthLocked]);

  // Get containers in the same row
  // For nested containers: siblings are from parent's nestedContainers
  // For top-level containers: siblings are from canvas's containers
  const containersInRow = useMemo((): ContainerAPI[] => {
    if (!canvasId || !container) return [];

    const currentRowIndex = container.metadata?.rowIndex ?? 0;

    // For nested containers, get siblings from parent's nestedContainers
    // For top-level containers, get siblings from canvas's containers
    const siblingContainers = isNestedContainer && parentContainer
      ? parentContainer.content?.nestedContainers || []
      : getContainersForCanvas(canvasId);

    // Filter to containers in the same row (excluding current container)
    return siblingContainers.filter(
      (c) =>
        c.id !== containerId &&
        (c.metadata?.rowIndex ?? 0) === currentRowIndex
    );
  }, [canvasId, container, containerId, isNestedContainer, parentContainer, getContainersForCanvas]);

  // Calculate max width based on containers in row
  // Each container must have at least 25%, so max = 100 - (numOtherContainers * 25)
  const maxWidth = useMemo((): number => {
    const numOtherContainers = containersInRow.length;
    // Minimum 25% per container, max 100%
    return Math.max(minWidthOption, 100 - numOtherContainers * minWidthOption);
  }, [containersInRow.length, minWidthOption]);

  /**
   * Build an UpdateContainerCommand for a width change.
   * Extracted so updateWidth can batch all sibling updates into one command.
   */
  const buildWidthCommand = useCallback(
    (targetId: string, newWidthStr: string) => {
      const { element: target, path: targetPath } = getContainer(targetId);
      if (!target || !targetPath?.canvasId) return null;
      return createUpdateContainerCommand(
        {
          containerId: targetId,
          canvasId: targetPath.canvasId,
          updates: {
            configuration: { ...target.configuration, width: newWidthStr },
          },
          previousValues: target,
        },
        `Update width of "${target.name}" to ${newWidthStr}`
      );
    },
    [getContainer]
  );

  /**
   * Update container width with auto-distribution to other containers.
   * Uses a single batch command so all sibling width changes are atomic
   * (one undo step, one dirty-mark, no React 17 batching issues).
   */
  const updateWidth = useCallback(
    (newWidth: number) => {
      if (!container || !canvasId) return;

      // Clamp to valid range
      const clampedWidth = Math.max(minWidthOption, Math.min(maxWidth, newWidth));

      // Build command for the current container
      const selfCmd = buildWidthCommand(containerId, formatWidth(clampedWidth));
      if (!selfCmd) return;

      // If no siblings, dispatch single command
      if (containersInRow.length === 0) {
        dispatch({ type: 'EXECUTE_COMMAND', payload: selfCmd });
        return;
      }

      // Calculate sibling widths
      const remainingWidth = 100 - clampedWidth;
      const unlockedContainers = containersInRow.filter(
        (c) => !c.configuration?.widthLocked
      );

      const siblingCommands = [];

      if (unlockedContainers.length > 0) {
        const lockedWidth = containersInRow
          .filter((c) => c.configuration?.widthLocked)
          .reduce((sum, c) => sum + parseWidth(c.configuration?.width), 0);

        const widthToDistribute = remainingWidth - lockedWidth;
        const widthPerUnlocked = Math.max(
          minWidthOption,
          widthToDistribute / unlockedContainers.length
        );

        for (const c of unlockedContainers) {
          const cmd = buildWidthCommand(c.id, formatWidth(widthPerUnlocked));
          if (cmd) siblingCommands.push(cmd);
        }
      }

      // Batch all width updates into a single command (atomic undo, single dirty-mark)
      const batch = createBatchCommand(
        [selfCmd, ...siblingCommands],
        `Resize "${container.name}" to ${formatWidth(clampedWidth)}`
      );
      dispatch({ type: 'EXECUTE_COMMAND', payload: batch });
    },
    [
      container,
      canvasId,
      containerId,
      containersInRow,
      maxWidth,
      minWidthOption,
      buildWidthCommand,
      dispatch,
    ]
  );

  // On unmount: commit any pending width change instead of discarding it
  const updateWidthRef = useRef(updateWidth);
  updateWidthRef.current = updateWidth;
  const localWidthRef = useRef(localWidth);
  localWidthRef.current = localWidth;

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        // Flush the pending width change so it isn't silently dropped
        updateWidthRef.current(localWidthRef.current);
      }
    };
  }, []);

  /**
   * Handle width change with debouncing
   */
  const handleWidthChange = useCallback(
    (value: number) => {
      // Update local state immediately for responsive UI
      setLocalWidth(value);

      // Clear existing timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Set new timer for debounced update
      debounceTimerRef.current = setTimeout(() => {
        updateWidth(value);
      }, debounceDelay);
    },
    [debounceDelay, updateWidth]
  );

  /**
   * Toggle lock state with validation
   *
   * Before locking, validates that the lock won't cause other unlocked
   * containers to be forced below minimum width.
   */
  const toggleLock = useCallback(() => {
    if (!container) return;

    // If trying to lock, validate first
    if (!isLocked) {
      const otherContainersData = containersInRow.map((c) => ({
        width: parseWidth(c.configuration?.width),
        isLocked: c.configuration?.widthLocked ?? false,
      }));

      const validation = validateLockImpact(localWidth, otherContainersData);

      if (!validation.canLock) {
        toast.warning(validation.reason ?? '');
        return;
      }
    }

    const newLocked = !isLocked;
    setIsLocked(newLocked);

    updateContainer(containerId, {
      configuration: {
        ...container.configuration,
        widthLocked: newLocked,
      },
    });
  }, [container, containerId, isLocked, localWidth, containersInRow, updateContainer]);

  /**
   * Reset all widths in the row to equal distribution
   */
  const resetRowWidths = useCallback(() => {
    if (!container || !canvasId) return;

    const allContainersInRow = [container, ...containersInRow];
    const equalWidth = 100 / allContainersInRow.length;

    allContainersInRow.forEach((c) => {
      updateContainer(c.id, {
        configuration: {
          ...c.configuration,
          width: formatWidth(equalWidth),
          widthLocked: false,
        },
      });
    });

    setLocalWidth(equalWidth);
    setIsLocked(false);
  }, [container, canvasId, containersInRow, updateContainer]);

  return {
    localWidth,
    isLocked,
    maxWidth,
    minWidth: minWidthOption,
    containersInRow,
    handleWidthChange,
    toggleLock,
    resetRowWidths,
    setLocalWidth,
  };
};

export default useContainerWidth;
